import type {
  ClientSticky,
  StickyDraft,
  StickyMeta,
  PendingSave,
} from 'views/EMR/Sticky/types';
import {
  draftsEqual,
  sortStickyIds,
} from 'views/EMR/Sticky/utils';
import {
  makeKey,
  SAVED_FLASH_MS,
} from 'views/EMR/Sticky/stores/constants';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';

export type StickyNotesCoreState = {
  activePatientId: number | null;
  persistedById: Record<number, ClientSticky>;
  orderIds: number[];
  isStickyNotesLoading: boolean;
  draftById: Record<number, StickyDraft>;
  metaById: Record<number, StickyMeta>;
  pendingSaves: Record<string, PendingSave>;
  editingStickyUniqueId: string | null;
};

export const initialStorageState: Partial<StickyNotesCoreState> = {
  persistedById: {},
  orderIds: [],
  draftById: {},
  metaById: {},
};

const defaultMeta: StickyMeta = {
  dirty: false,
  saving: false,
  error: null,
};

export function createInitialDraftState(
  state: StickyNotesCoreState,
  stickyId: number,
): Partial<StickyNotesCoreState> | null {
  const { draftById, persistedById, metaById } = state;
  const persisted = persistedById[stickyId];

  // return null if draft exists or persisted does not exist
  return (!isNil(draftById[stickyId]) || isNil(persistedById[stickyId])) ? null : {
    draftById: {
      ...draftById,
      [stickyId]: {
        title: persisted?.title ?? '',
        content: persisted?.content ?? '',
        isPinned: persisted?.isPinned ?? false,
      },
    },
    metaById: {
      ...metaById,
      [stickyId]: {
        ...defaultMeta,
      },
    },
  };
}

export function applyDraftPatchState(
  state: StickyNotesCoreState,
  stickyId: number,
  patch: Partial<StickyDraft>,
): Partial<StickyNotesCoreState> | null {
  const { draftById, persistedById, metaById } = state;
  const persisted = persistedById[stickyId];

  const prevDraft = draftById[stickyId] ?? {
    title: persisted?.title ?? '',
    content: persisted?.content ?? '',
    isPinned: persisted?.isPinned ?? false,
  };

  const nextDraft: StickyDraft = { ...prevDraft, ...patch };

  const dirty = !draftsEqual(nextDraft, {
    title: persisted?.title ?? '',
    content: persisted?.content ?? '',
    isPinned: persisted?.isPinned ?? false,
  });

  return !isNil(persisted) ? {
    draftById: { ...draftById, [stickyId]: nextDraft },
    metaById: {
      ...metaById,
      [stickyId]: {
        ...(metaById[stickyId] ?? defaultMeta),
        dirty,
        hasRemoteUpdateWhileEditing: dirty
          ? metaById[stickyId]?.hasRemoteUpdateWhileEditing : false,
      },
    },
  } : null;
}

export function setLoadingState(isStickyNotesLoading: boolean): Partial<StickyNotesCoreState> {
  return { isStickyNotesLoading };
}

export function normalizeStickiesForPatient(
  patientId: number,
  list: ClientSticky[],
): Partial<StickyNotesCoreState> {
  const persistedById: Record<number, ClientSticky> = {};
  list.forEach((sticky) => {
    if (sticky.stickyId) {
      persistedById[sticky.stickyId] = {
        ...sticky,
        // For existing stickies, use stickyId as uniqueId (they're stable)
        uniqueId: sticky?.stickyId.toString(),
      };
    }
  });

  return {
    activePatientId: patientId,
    persistedById,
    orderIds: sortStickyIds(persistedById),
    isStickyNotesLoading: false,
    draftById: {},
    metaById: {},
  };
}

export function markSavingState(
  state: StickyNotesCoreState,
  stickyId: number,
): Partial<StickyNotesCoreState> {
  return {
    metaById: {
      ...state.metaById,
      [stickyId]: {
        ...(state.metaById[stickyId] ?? { dirty: true, saving: false }),
        saving: true,
        error: null,
      },
    },
  };
}

export function markSaveErrorState(
  state: StickyNotesCoreState,
  stickyId: number,
  message: string,
): Partial<StickyNotesCoreState> {
  return {
    metaById: {
      ...state.metaById,
      [stickyId]: {
        ...(state.metaById[stickyId] ?? { dirty: true, saving: false }),
        saving: false,
        error: message,
      },
    },
  };
}

export function applyCreateSuccessState(params: {
  state: StickyNotesCoreState;
  tempStickyId: number;
  newSticky: ClientSticky;
  // preserve existing uniqueId from the temp persisted record
  preservedUniqueId?: string;
}): Partial<StickyNotesCoreState> {
  const {
    state,
    tempStickyId,
    newSticky,
    preservedUniqueId,
  } = params;

  const nextPersisted = { ...state.persistedById };
  delete nextPersisted[tempStickyId];

  if (!isNil(newSticky?.stickyId) && preservedUniqueId) {
    nextPersisted[newSticky.stickyId] = {
      ...newSticky,
      uniqueId: preservedUniqueId,
    };
  }

  const nextDraft = { ...state.draftById };
  delete nextDraft[tempStickyId];

  const nextMeta = { ...state.metaById };
  delete nextMeta[tempStickyId];

  if (!isNil(newSticky?.stickyId)) {
    nextMeta[newSticky.stickyId] = {
      dirty: false,
      saving: false,
      error: null,
      savedFlashUntil: Date.now() + SAVED_FLASH_MS,
    };
  }

  const nextOrderIds = state.editingStickyUniqueId
    ? state.orderIds.map((id) => (id === tempStickyId ? newSticky.stickyId! : id))
    : sortStickyIds(nextPersisted);

  return {
    persistedById: nextPersisted,
    orderIds: nextOrderIds,
    draftById: nextDraft,
    metaById: nextMeta,
  };
}

export function applyUpdateSuccessState(params: {
  state: StickyNotesCoreState;
  stickyId: number;
  updated: ClientSticky;
}): Partial<StickyNotesCoreState> {
  const { state, stickyId, updated } = params;

  const existingUnique = state.persistedById[stickyId]?.uniqueId;
  const draft = state.draftById[stickyId];

  const isDirty = !isNil(draft) && !draftsEqual(draft, {
    title: updated.title ?? '',
    content: updated.content ?? '',
    isPinned: !!updated.isPinned,
  });

  const nextPersistedById = {
    ...state.persistedById,
    [stickyId]: {
      ...updated,
      uniqueId: existingUnique || stickyId.toString(),
    },
  };

  return {
    persistedById: nextPersistedById,
    orderIds: state.editingStickyUniqueId ? state.orderIds : sortStickyIds(nextPersistedById),
    metaById: {
      ...state.metaById,
      [stickyId]: {
        ...(state.metaById[stickyId] ?? { dirty: false, saving: false }),
        ...defaultMeta,
        dirty: isDirty,
        savedFlashUntil: Date.now() + SAVED_FLASH_MS,
        // Clear the remote update flag after successful save
        hasRemoteUpdateWhileEditing: false,
      },
    },
  };
}

export function removeStickyLocalState(params: {
  state: StickyNotesCoreState;
  stickyId: number;
}): Partial<StickyNotesCoreState> {
  const { state, stickyId } = params;

  const persistedById = { ...state.persistedById };
  delete persistedById[stickyId];

  const draftById = { ...state.draftById };
  delete draftById[stickyId];

  const metaById = { ...state.metaById };
  delete metaById[stickyId];

  return {
    persistedById,
    orderIds: !isNil(state.editingStickyUniqueId)
      ? state.orderIds
      : sortStickyIds(persistedById),
    draftById,
    metaById,
  };
}

export function isValidDraft(draft: StickyDraft): boolean {
  return notEmptyString(draft?.title ?? '');
}

export function getDraftStickiesForPendingSaves(
  state: StickyNotesCoreState,
): Partial<StickyNotesCoreState> | null {
  const {
    activePatientId,
    metaById,
    draftById,
    pendingSaves,
  } = state;

  const nextPending = { ...pendingSaves };

  Object.entries(metaById).forEach(([idStr, meta]) => {
    const stickyId = Number(idStr);
    const draft = draftById[stickyId];
    const shouldSkip = !meta?.dirty || !isValidDraft(draft) || isNil(draft) || meta?.saving;

    if (!shouldSkip) {
      nextPending[makeKey(activePatientId ?? 0, stickyId)] = {
        patientId: activePatientId ?? 0,
        stickyId,
        operationType: 'update',
        payload: draft,
      };
    }
  });

  return isNil(activePatientId) ? null : { pendingSaves: nextPending };
}

export function applySignalRUpsertState(params: {
  state: StickyNotesCoreState;
  stickyId: number;
  fresh: ClientSticky;
}): Partial<StickyNotesCoreState> {
  const { state, stickyId, fresh } = params;

  const existingUnique = state.persistedById[stickyId]?.uniqueId;

  const nextPersistedById = {
    ...state.persistedById,
    [stickyId]: {
      ...fresh,
      uniqueId: existingUnique || stickyId.toString(),
    },
  };
  // Keep draft when user has local changes OR when they're actively editing this sticky
  const keepDraft = state.metaById[stickyId]?.dirty
    || state.editingStickyUniqueId === existingUnique;

  return {
    persistedById: nextPersistedById,
    orderIds: state.editingStickyUniqueId ? state.orderIds : sortStickyIds(nextPersistedById),
    // Sync draft with fresh data when user isn't editing
    draftById: keepDraft
      ? state.draftById
      : {
        ...state.draftById,
        [stickyId]: {
          title: fresh?.title ?? '',
          content: fresh?.content ?? '',
          isPinned: fresh?.isPinned ?? false,
        },
      },
  };
}

export function resetDraftToPersistedState(
  state: StickyNotesCoreState,
  stickyId: number,
): Partial<StickyNotesCoreState> {
  const persisted = state.persistedById[stickyId];

  return !isNil(persisted) ? {
    draftById: {
      ...state.draftById,
      [stickyId]: {
        title: persisted?.title ?? '',
        content: persisted?.content ?? '',
        isPinned: persisted?.isPinned ?? false,
      },
    },
    metaById: {
      ...state.metaById,
      [stickyId]: {
        ...(state.metaById[stickyId] ?? { saving: false, error: null }),
        dirty: false,
        hasRemoteUpdateWhileEditing: false,
      },
    },
  } : {};
}
